/* eslint-disable no-negated-condition */
import * as yup from 'yup';
import { EmergencyNumbers } from 'constants/phone-number';

export const formatToPhoneNumber = (str) => {
  if (!str) {
    return '';
  }

  const pattern = /(\d{3})(\d{3})(\d{4})/;

  if (typeof str === 'string' && str.match(pattern)) {
    return str.replace(pattern, '($1) $2-$3');
  }

  return str;
};

const PHONE_NUMBER_REQUIRED = 'Phone number is required.';
const INVALID_PHONE_NUMBER =
  'Phone number may only contain +, -, and 0-9. Only the leading character may be a + or *.';

yup.addMethod(yup.object, 'uniqueProperty', function (propertyName, message) {
  return this.test('unique', message, function (value) {
    if (!value || !value[propertyName]) {
      return true;
    }

    const { path } = this;
    const options = [...this.parent];
    const currentIndex = options.indexOf(value);

    const subOptions = options.slice(0, currentIndex);

    if (
      subOptions.some((option) => option[propertyName] === value[propertyName])
    ) {
      throw this.createError({
        path: `${path}.${propertyName}`,
        message,
      });
    }

    return true;
  });
});

export const formSchema = (dispatch) => {
  return yup.object().shape({
    always: yup.object().shape({
      enabled: yup.boolean().required(),
      phoneNumber: yup.lazy((value) =>
        !value
          ? yup.string()
          : yup
              .string()
              .matches(/^(?:\+|\*|\d)[\d ()-]+$/, INVALID_PHONE_NUMBER),
      ),
    }),
    busy: yup.object().shape({
      enabled: yup.boolean().required(),
      phoneNumber: yup.lazy((value) =>
        !value
          ? yup.string()
          : yup
              .string()
              .matches(/^(?:\+|\*|\d)[\d ()-]+$/, INVALID_PHONE_NUMBER),
      ),
    }),
    selective: yup.object().shape({
      enabled: yup.boolean().required(),
      phoneNumber: yup.string().when(['conditionList'], {
        is: (conditionList) => conditionList?.length > 0,
        then: yup.lazy((value) => {
          if (!value) {
            dispatch({
              type: 'SNACKBAR_SET',
              payload: {
                message:
                  'Missing required information. There must be a phone number and one active condition to enable call forwarding selective.',
                type: 'error',
              },
            });
          }

          return yup
            .string()
            .required(PHONE_NUMBER_REQUIRED)
            .matches(/^(?:\+|\*|\d)[\d ()-]+$/, INVALID_PHONE_NUMBER);
        }),
        otherwise: yup.lazy((value) =>
          !value
            ? yup.string()
            : yup
                .string()
                .matches(/^(?:\+|\*|\d)[\d ()-]+$/, INVALID_PHONE_NUMBER),
        ),
      }),
    }),

    unanswered: yup.object().shape({
      enabled: yup.boolean().required(),
      forwardTimeoutSeconds: yup
        .string()
        .matches(/^[0-9]*$/, 'Seconds must be a number.')
        .required('Seconds are required.')
        .min(0)
        .max(7200)
        .label('Number of seconds'),
      forwardToPhoneNumber: yup.string().when(['forwardToVoicemail'], {
        is: (forwardToVoicemail) => forwardToVoicemail === 'true',
        then: yup.string(),
        otherwise: yup.lazy((value) =>
          !value
            ? yup.string()
            : yup
                .string()
                .matches(/^(?:\+|\*|\d)[\d ()-]+$/, INVALID_PHONE_NUMBER),
        ),
      }),
    }),
  });
};

export const drawerSchema = (conditionList) => {
  return yup.object().shape({
    callFrom: yup.array().of(
      yup.object().shape({
        phoneNumber: yup
          .string()
          .required('Add phone number or click remove to save.')
          .matches(
            /^[\d ()+-]+$/,
            'Please provide a valid extension or phone number.',
          ),
      }),
    ),
    forwardFromAnyPhoneNumber: yup.lazy((value) => {
      if (value === 'true') {
        return yup.string().test({ name: 'test1', test: (v) => v === 'true' });
      }

      return yup
        .string()
        .when(
          [
            'forwardFromAnyPrivateNumber',
            'forwardFromAnyUnavailableNumber',
            'callFrom',
          ],
          {
            is: (
              forwardFromAnyPrivateNumber,
              forwardFromAnyUnavailableNumber,
              callFrom,
            ) => {
              return (
                !forwardFromAnyPrivateNumber &&
                !forwardFromAnyUnavailableNumber &&
                callFrom?.length === 0
              );
            },
            then: () =>
              yup.string().test({
                name: 'phone number field array test',
                test: (v) => v === 'true',
                message:
                  'Please provide at least one phone number, unless "Any private number" or "Any unavailable number" is selected.',
              }),
            otherwise: yup.string(),
          },
        );
    }),
    forwardOption: yup.string(),
    forwardTo: yup.string().when(['forwardOption'], {
      is: (forwardOption) => forwardOption === 'Forward To Specified Number',
      then: () =>
        yup
          .string()
          .required(PHONE_NUMBER_REQUIRED)
          .matches(
            /^[\d ()+-]+$/,
            'Please provide a valid extension or phone number.',
          ),
      otherwise: yup.string(),
    }),
    name: yup.lazy((value) => {
      if (value) {
        return yup.string().test({
          name: 'condition name test',
          test: (v) => !conditionList.some((c) => c.name.trim() === v.trim()),
          message: 'A condition with this name already exists.',
        });
      }

      return yup.string();
    }),
  });
};

export const requiredSchema = (showSpecifiedNumber) => {
  return yup.object().shape({
    forwardOption: yup.string().required(),
    forwardTo: yup.lazy(() =>
      showSpecifiedNumber ? yup.string().required() : yup.string(),
    ),
    name: yup.string().required(),
    schedule: yup.object().shape({
      scheduleLevel: yup.string(),
      scheduleName: yup.string().required(),
    }),
  });
};

export const teamPhoneNumberSchema = () => {
  return yup.object().shape({
    alternatePhoneNumbers: yup.array().of(
      yup
        .object()
        .shape({
          extension: yup
            .string()
            .test(
              'Extension Test',
              'Cannot be emergency #',
              (value) => !EmergencyNumbers.includes(value),
            )
            .matches(/^[0-9]*$/, 'Please provide a valid extension.'),
        })
        .uniqueProperty('extension', `Duplicate extension is not allowed.`),
    ),
    primaryPhoneNumber: yup.object().shape({
      extension: yup
        .string()
        .required('Extension required.')
        .test(
          'Extension Test',
          'Cannot be emergency #',
          (value) => !EmergencyNumbers.includes(value),
        ),
    }),
  });
};
